import { TranslatedString, TranslationFunction } from '../../components/blocks';
import { Address } from '../../redux/types';
import { ExtractionFinding } from '../../types';
import { addressFindingsToRender, isDateOutsideWindowFinding } from './poReviewFindings';

export type ExtractedHeaderField = {
    value: string;
    notRead?: boolean;
    rawValue?: string;
    findings: ExtractionFinding[];
};

export type HeaderFieldState = {
    notReadText?: TranslatedString;
    findings: ExtractionFinding[];
};

export type ShippingLocationInput = {
    printedAddress?: string;
    findings: ExtractionFinding[];
    locationCode?: string;
    addressMap: Record<string, Address>;
};

export type ShippingLocationState = {
    hasLocation: boolean;
    showsAddressNotRead: boolean;
    findings: ExtractionFinding[];
};

function headerFieldState(
    field: ExtractedHeaderField,
    notReadMessage: (rawValue?: string) => TranslatedString
): HeaderFieldState {
    const findings = field.rawValue ? [] : field.findings;
    const explainsEmptyField =
        Boolean(field.notRead) && !field.value && (Boolean(field.rawValue) || field.findings.length === 0);
    return {
        notReadText: explainsEmptyField ? notReadMessage(field.rawValue) : undefined,
        findings,
    };
}

function dateFieldFindings(findings: ExtractionFinding[], dateConcernActive: boolean): ExtractionFinding[] {
    return dateConcernActive ? findings : findings.filter((finding) => !isDateOutsideWindowFinding(finding));
}

export function poNumberFieldState(t: TranslationFunction, field: ExtractedHeaderField): HeaderFieldState {
    return headerFieldState(field, (rawValue) =>
        rawValue ? t('poNumberUnusable-text', { value: rawValue }) : t('poNumberUnreadable-text')
    );
}

export function shipDateFieldState(
    t: TranslationFunction,
    field: ExtractedHeaderField,
    shipDateInPast: boolean,
    shipDateConcernActive: boolean
): HeaderFieldState {
    const findings = dateFieldFindings(field.findings, shipDateConcernActive);
    return headerFieldState({ ...field, findings }, (rawValue) => {
        if (!rawValue) {
            return t('shipDateNotRead-text');
        }
        return shipDateInPast
            ? t('shipDateInPast-text', { value: rawValue })
            : t('shipDateUnusable-text', { value: rawValue });
    });
}

export function deliveryDateFieldState(
    t: TranslationFunction,
    field: ExtractedHeaderField,
    deliveryDateInPast: boolean,
    deliveryDateConcernActive: boolean
): HeaderFieldState {
    if (!field.rawValue) {
        return { findings: dateFieldFindings(field.findings, deliveryDateConcernActive) };
    }
    if (field.value) {
        return { findings: [] };
    }
    const notReadText = deliveryDateInPast
        ? t('deliveryDateInPast-text', { value: field.rawValue })
        : t('deliveryDateUnusable-text', { value: field.rawValue });
    return { notReadText, findings: [] };
}

// A location code the reseller cannot see is treated as no location, so a stale code never submits.
export function shippingLocationState(input: ShippingLocationInput): ShippingLocationState {
    const findings = addressFindingsToRender(input.findings);
    const hasLocation = Boolean(input.locationCode && input.addressMap[input.locationCode]);
    const showsAddressNotRead = !input.printedAddress && !hasLocation && findings.length === 0;
    return { hasLocation, showsAddressNotRead, findings };
}
